// styles
import Styled from './AirlineFilter.styled';

// hooks
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useAirlineFilter from '@/hooks/useAirlineFilter';

// utils
import _map from 'lodash/map';
import _size from 'lodash/size';
import { buildTestId } from '@/utils/helpers.utils';
import { getLogoImg } from '@/utils/content.utils';

// types
import type MultiSelectFilter from '@tajawal/web-flights-sdk/lib/stores/filter/base/multi-select-filter.store';
import { AIRLINE_SHOW_COUNT } from '@/constants/common.constants';

// components
import { COLOR_VARIANTS, Checkbox, Link, Icons } from '@tajawal/ct-web-design-system';

export type AirlineFilterProps = {
  testIdPrefix: string;
  filter: MultiSelectFilter;
  $resources: any;
};

function AirlineFilter({ testIdPrefix, filter, $resources }: AirlineFilterProps): JSX.Element {
  const testId = buildTestId(testIdPrefix, 'AirlineFilter');
  const { t } = useTranslation();
  const [airlineShowCount, setAirlineShowCount] = useState(AIRLINE_SHOW_COUNT);
  const { state, handleToggleOption } = useAirlineFilter(filter);
  const isAirlineFullyShown = airlineShowCount === _size(state);
  const ChevronIcon = isAirlineFullyShown ? Icons.ChevronUp : Icons.ChevronDown;
  const logos = useMemo(
    () =>
      _map(state, ({ code }) => {
        return getLogoImg(String(code));
      }),
    [state]
  );

  // @ts-ignore
  return (
    <Styled.Wrapper>
      {_map(state.slice(0, airlineShowCount), ({ code, selected, label }, index) => {
        return (
          <Checkbox
            isSingle
            addMargin
            testId={buildTestId(testId, `Option_${code}`)}
            key={label}
            onChange={() => handleToggleOption({ code, selected })}
            label={$resources.getAirlineLabel(label)}
            isChecked={selected}
            // @ts-ignore what they exposed was an SVGRectElement, but the actual type React Element
            icon={<Styled.AirlineImage height="24px" width="24px" src={logos[index]} />}
          />
        );
      })}
      {_size(state) > AIRLINE_SHOW_COUNT && (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <Link
          onClick={() => setAirlineShowCount(airlineShowCount === _size(state) ? AIRLINE_SHOW_COUNT : _size(state))}
          testId={buildTestId(testId, 'Show_All')}
          color={COLOR_VARIANTS.AQUA100}
        >
          <Styled.AirlineShowLink>
            <span> {isAirlineFullyShown ? t('Filters.Airlines_View_Less') : t('Filters.Airlines_View_All')}</span>
            <ChevronIcon cursor="pointer" width="16px" height="16px" />
          </Styled.AirlineShowLink>
        </Link>
      )}
    </Styled.Wrapper>
  );
}

export default AirlineFilter;
